import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAccount, useConfig } from "wagmi";
import { getAccount, getPublicClient, getWalletClient, switchChain, waitForTransactionReceipt } from "wagmi/actions";
import {
  decodeFunctionData,
  encodeFunctionData,
  parseAbiItem,
  toFunctionSelector,
  type Address,
  type GetLogsReturnType,
  type PublicClient,
} from "viem";
import { keelTimelockAbi, keelVaultFactoryAbi } from "../abi";
import { clienteLogs } from "../lib/cliente";
import type { RedKeel } from "../config/networks";
import { traducir } from "../lib/errores";

/**
 * El gobierno de la factory, desde la wallet.
 *
 * POR QUE EXISTE. Desde el 2026-08-14 el `admin` de las factories es un `KeelTimelock` cuyo dueño es
 * la wallet personal, no la llave del VPS. `script/timelock.sh` firma con `TIMELOCK_PK`, o sea con la
 * clave privada en una variable de entorno — que es justo lo que no hay que hacer con la llave que
 * gobierna los contratos y que ademas posee los vaults. Desde aqui la firma la da la wallet: la clave
 * no sale de ella ni pasa por ningun fichero.
 *
 * EL TIMELOCK NO SE CABLEA. Se lee de `factory.admin()`, que es la definicion de quien gobierna esa
 * factory. Una lista escrita a mano se quedaria vieja el dia que se mueva el gobierno, y lo haria en
 * silencio: la pantalla apuntaria a un timelock que ya no manda y las propuestas no harian nada.
 */
export type EstadoGobierno = {
  timelock: Address;
  duenoDelTimelock: Address;
  puedoGobernar: boolean;
  delaySegundos: number;
  graciaSegundos: number;
  /** lo que la factory publica AHORA */
  implActual: Address;
  logicaActual: Address;
};

export function useGobierno(red: RedKeel | undefined) {
  const { address } = useAccount();
  const config = useConfig();

  return useQuery({
    queryKey: ["gobierno", red?.clave, address],
    enabled: !!red && !!address,
    refetchInterval: 60_000,
    queryFn: async (): Promise<EstadoGobierno> => {
      const cliente = getPublicClient(config, { chainId: red!.idCadena })!;
      const f = { address: red!.factory, abi: keelVaultFactoryAbi } as const;

      const [admin, implActual, logicaActual] = await cliente.multicall({
        allowFailure: false,
        contracts: [
          { ...f, functionName: "admin" },
          { ...f, functionName: "implementation" },
          { ...f, functionName: "rebalancer" },
        ],
      });

      const t = { address: admin as Address, abi: keelTimelockAbi } as const;
      // Si el admin NO es un timelock (una factory vieja con la hot key de admin), estas tres
      // llamadas revientan: se devuelve el estado "no hay gobierno que enseñar" en vez de romper.
      const [dueno, delay, gracia] = await cliente
        .multicall({
          allowFailure: false,
          contracts: [
            { ...t, functionName: "owner" },
            { ...t, functionName: "delay" },
            { ...t, functionName: "gracia" },
          ],
        })
        .catch(() => ["0x0000000000000000000000000000000000000000" as Address, 0, 0] as const);

      return {
        timelock: admin as Address,
        duenoDelTimelock: dueno as Address,
        puedoGobernar: !!address && (dueno as string).toLowerCase() === address.toLowerCase(),
        delaySegundos: Number(delay),
        graciaSegundos: Number(gracia),
        implActual: implActual as Address,
        logicaActual: logicaActual as Address,
      };
    },
  });
}

export type EstadoPropuesta = {
  encolada: boolean;
  /** segundos que faltan para poder ejecutar (0 si ya se puede) */
  faltan: number;
  lista: boolean;
  caducada: boolean;
  id: `0x${string}`;
};

/**
 * ¿Esas dos direcciones son de verdad NUESTROS contratos, EN ESTA RED?
 *
 * LA COMPROBACIÓN QUE EVITA EL DESASTRE. `setLogic` acepta cualquier dirección: no mira nada. Pegar
 * las direcciones desplegadas en OTRA cadena es el error natural —se despliega en varias el mismo
 * día y todas empiezan por 0x—, y no falla al firmar: falla después, con dinero dentro.
 *
 * NO BASTA CON MIRAR SI HAY CÓDIGO, y esto se comprobó en cadena el 2026-08-15: la dirección de la
 * lógica de Arbitrum, mirada en Robinhood, resultó tener un contrato de 10.286 bytes que no es
 * nuestro. Un "sí, hay código ahí" habría dado luz verde a publicar un contrato ajeno como la lógica
 * que mueve el dinero de todos los vaults nuevos.
 *
 * Por eso se pregunta por algo que SOLO tienen los nuestros:
 *   · la lógica tiene que responder `CONVERT_FEE_BPS()` — solo existe en KeelRebalancer;
 *   · la implementación tiene que llevar `withdrawTo(bool)` en su bytecode, y NO responder
 *     `CONVERT_FEE_BPS` (si lo hace, están puestas al revés).
 */
export function useTienenCodigo(red: RedKeel | undefined, impl: string, logica: string) {
  const config = useConfig();
  const valida = /^0x[0-9a-fA-F]{40}$/.test(impl) && /^0x[0-9a-fA-F]{40}$/.test(logica);

  return useQuery({
    queryKey: ["codigo-gobierno", red?.clave, impl, logica],
    enabled: !!red && valida,
    staleTime: 60_000,
    queryFn: async () => {
      const cliente = getPublicClient(config, { chainId: red!.idCadena })!;
      const abiFee = [{
        type: "function", name: "CONVERT_FEE_BPS", stateMutability: "view",
        inputs: [], outputs: [{ type: "uint16" }],
      }] as const;
      const responde = (a: string) =>
        cliente
          .readContract({ address: a as Address, abi: abiFee, functionName: "CONVERT_FEE_BPS" })
          .then(() => true)
          .catch(() => false);

      const [cImpl, cLogica, feeEnImpl, feeEnLogica] = await Promise.all([
        cliente.getCode({ address: impl as Address }),
        cliente.getCode({ address: logica as Address }),
        responde(impl),
        responde(logica),
      ]);
      const hay = (c: `0x${string}` | undefined) => !!c && c !== "0x";

      // El selector viaja dentro del bytecode del contrato: si la función existe, está ahí.
      const selWithdrawTo = toFunctionSelector("withdrawTo(bool)").slice(2);

      return {
        implTieneCodigo: hay(cImpl),
        logicaTieneCodigo: hay(cLogica),
        implEsVault: hay(cImpl) && (cImpl as string).includes(selWithdrawTo),
        logicaEsRebalancer: feeEnLogica,
        pareceIntercambiado: feeEnImpl,
      };
    },
  });
}

/** El estado de UNA propuesta concreta de `setLogic`, preguntándoselo al contrato. */
export function usePropuesta(
  red: RedKeel | undefined,
  timelock: Address | undefined,
  impl: string,
  logica: string
) {
  const config = useConfig();
  const valida = /^0x[0-9a-fA-F]{40}$/.test(impl) && /^0x[0-9a-fA-F]{40}$/.test(logica);

  return useQuery({
    queryKey: ["propuesta", red?.clave, timelock, impl, logica],
    enabled: !!red && !!timelock && valida,
    refetchInterval: 30_000,
    queryFn: async (): Promise<EstadoPropuesta> => {
      const cliente = getPublicClient(config, { chainId: red!.idCadena })!;
      const datos = encodeFunctionData({
        abi: keelVaultFactoryAbi,
        functionName: "setLogic",
        args: [impl as Address, logica as Address],
      });
      const t = { address: timelock!, abi: keelTimelockAbi } as const;

      // El id lo calcula EL CONTRATO. Reproducir aqui el `keccak(abi.encode(destino, datos))` seria
      // una segunda implementacion de la misma regla, y el dia que difirieran en un byte la pantalla
      // diria "no hay nada encolado" sobre una propuesta que si existe.
      const id = await cliente.readContract({
        ...t, functionName: "id", args: [red!.factory, datos],
      });
      const [encolada, cuando, lista, caducada] = await cliente.readContract({
        ...t, functionName: "estado", args: [id],
      });

      const ahora = Math.floor(Date.now() / 1000);
      return {
        encolada,
        faltan: Math.max(0, Number(cuando) - ahora),
        lista,
        caducada,
        id,
      };
    },
  });
}

const ENCOLADA = parseAbiItem(
  "event Encolada(bytes32 indexed id, address indexed destino, bytes datos, uint256 cuando)"
);

export type Pendiente = {
  id: `0x${string}`;
  destino: Address;
  datos: `0x${string}`;
  /** momento (en segundos) a partir del cual se puede aplicar */
  cuando: number;
  /** momento en el que caduca y hay que volver a anunciarla */
  caducaEn: number;
  lista: boolean;
  caducada: boolean;
  /** qué hace la llamada, en una línea */
  titulo: string;
  /** los argumentos, ya legibles */
  detalle: string[];
  /** el destino NO es la factory de esta red: merece que se mire dos veces */
  destinoRaro: boolean;
  /** hay otra propuesta viva que hace LO MISMO con otros valores, y se pisan */
  rival: boolean;
  /** teniendo rivales, esta es la anunciada más tarde */
  masNueva: boolean;
  /** publica una implementación que la app NO reconoce (no está en `implsConGestion`) */
  implDesconocida: boolean;
  /** grave de verdad: cede el gobierno a otra llave */
  cedeElMando: boolean;
};

/**
 * Traduce el calldata de una propuesta a algo que se pueda leer sin un decodificador al lado.
 *
 * SIN ESTO LA COLA ES INSERVIBLE. Lo único que el timelock guarda en almacenamiento es el id —un
 * hash— y la hora; el qué vive en el evento, como un `bytes` en crudo. Una pantalla que enseñe
 * `0x1ba0ac66000000…` obliga a decodificar a mano justo antes de firmar el cambio de gobierno, que
 * es el peor momento posible para equivocarse.
 */
function traducirLlamada(
  datos: `0x${string}`,
  red: RedKeel
): { titulo: string; detalle: string[]; cedeElMando: boolean; implPublicada?: Address } {
  const bps = (v: bigint | number) => `${Number(v) / 100}%`;
  try {
    const d = decodeFunctionData({ abi: keelVaultFactoryAbi, data: datos });
    switch (d.functionName) {
      case "setLogic":
        return {
          titulo: "Publicar lógica nueva en la factory",
          detalle: [
            `KeelVault (implementación): ${d.args![0]}`,
            `KeelRebalancer (lógica): ${d.args![1]}`,
            "Los vaults que ya existen NO cambian: solo con qué código nacen los nuevos.",
          ],
          cedeElMando: false,
          implPublicada: d.args![0] as Address,
        };
      case "setTreasury":
        return {
          titulo: "Cambiar a dónde va la comisión",
          detalle: [`Treasury nuevo: ${d.args![0]}`],
          cedeElMando: false,
        };
      case "setPerfFee":
        return {
          titulo: `Cambiar la comisión de rendimiento a ${bps(d.args![0] as number)}`,
          detalle: [`${String(d.args![0])} puntos básicos sobre las ganancias`],
          cedeElMando: false,
        };
      case "setDefaultOperator":
        return {
          titulo: "Cambiar el operador con el que nacen los vaults",
          detalle: [`Operador nuevo: ${d.args![0]}`],
          cedeElMando: false,
        };
      case "setAdmin":
        return {
          titulo: "CEDER EL GOBIERNO de la factory",
          detalle: [
            `Admin nuevo: ${d.args![0]}`,
            "Después de esto, este timelock ya no manda en la factory.",
          ],
          cedeElMando: true,
        };
    }
  } catch { /* no es una llamada a la factory: se prueba con el timelock */ }

  try {
    const d = decodeFunctionData({ abi: keelTimelockAbi, data: datos });
    if (d.functionName === "setOwner") {
      return {
        titulo: "CEDER EL TIMELOCK a otra llave",
        detalle: [`Dueño nuevo: ${d.args![0]}`, "Quien mande ahí podrá anunciar y aplicar cambios."],
        cedeElMando: true,
      };
    }
    return { titulo: `Timelock · ${d.functionName}`, detalle: [], cedeElMando: false };
  } catch { /* tampoco */ }

  // Ni la factory ni el timelock: no se adivina, se dice. Ocultar que no se entiende sería peor.
  return {
    titulo: "Llamada que esta pantalla no sabe leer",
    detalle: [
      `Datos en crudo: ${datos.slice(0, 42)}…`,
      `Compruébala en el explorador de ${red.etiqueta} antes de aplicarla.`,
    ],
    cedeElMando: false,
  };
}

/**
 * Peticiones de logs como máximo cuando hay que paginar. Con tramos de 9.000 bloques, 170 cubren de
 * sobra los quince días de ventana viva de HyperEVM (`delay + gracia`), que es lo único que puede
 * seguir anunciado. Se puede permitir ese número porque el historial se lee UNA VEZ y se cachea: lo
 * que se repite cada minuto es el estado, que es un solo multicall. Si aun así no alcanzara, la cola
 * se marca incompleta y la pantalla lo dice. Ver `leerEncoladas`.
 */
const TOPE_TRAMOS = 170;

type LogEncolada = GetLogsReturnType<typeof ENCOLADA>[number];

/**
 * Lee los `Encolada` del timelock, sobreviviendo a los RPC que no dejan mirar el historial entero.
 *
 * EL CAMINO RÁPIDO ES UNA SOLA PETICIÓN desde el bloque 0, y es lo que hacen Arbitrum y Robinhood.
 * HyperEVM no: medido el 2026-08-17, ninguno de sus cuatro RPC públicos acepta un rango mayor de
 * ~10.000 bloques —ni el oficial, que rechaza cualquier `eth_getLogs` con «Invalid parameters»—, y
 * allí 1.000 bloques son solo 16 minutos de historia. Sin paginar, la cola de esa red salía vacía:
 * la pantalla decía «no hay nada anunciado» sobre propuestas que existen, que es la peor mentira
 * posible en un gobierno con retardo.
 *
 * Se pagina hacia atrás hasta cubrir `ventanaSegundos`, que es lo único que puede seguir vivo, y
 * con un tope de peticiones: una pantalla no puede lanzar cientos de llamadas contra un RPC público.
 * Si el tope se agota antes de cubrir la ventana se devuelve `completa: false` y la pantalla LO
 * DICE, en vez de dar por buena una lista que quizá no lo está.
 */
async function leerEncoladas(
  cliente: PublicClient,
  timelock: Address,
  ventanaSegundos: number
): Promise<{ logs: LogEncolada[]; completa: boolean }> {
  const filtro = { address: timelock, event: ENCOLADA } as const;
  try {
    const logs = (await cliente.getLogs({ ...filtro, fromBlock: 0n, toBlock: "latest" })) as LogEncolada[];
    return { logs, completa: true };
  } catch { /* este RPC no deja: se pagina */ }

  const ultimo = await cliente.getBlockNumber();
  // El ritmo se MIDE, no se cablea: cada red tiene el suyo y el de HyperEVM ha cambiado. Con un
  // número inventado, la ventana se quedaría corta o se irían cientos de peticiones al vacío.
  let segPorBloque = 1;
  try {
    const [a, b] = await Promise.all([
      cliente.getBlock({ blockNumber: ultimo }),
      cliente.getBlock({ blockNumber: ultimo - 1000n }),
    ]);
    const d = Number(a.timestamp - b.timestamp) / 1000;
    if (d > 0) segPorBloque = d;
  } catch { /* con 1 s/bloque se cubre de más, que es el lado seguro */ }

  const TRAMO = 9000n; // por debajo del límite de ~10.000 que acepta el RPC de HyperEVM
  const aCubrir = BigInt(Math.ceil((ventanaSegundos || 15 * 86400) / segPorBloque));
  const suelo = ultimo > aCubrir ? ultimo - aCubrir : 0n;

  // SE INTENTÓ ACORTAR ESTO BUSCANDO EL BLOQUE EN QUE NACIÓ EL TIMELOCK —antes de él no hay nada que
  // leer— con una binaria de `getCode` por bloque. NO FUNCIONA EN HYPEREVM: sus RPC públicos no son
  // de archivo y contestan el estado de HOY para cualquier bloque pasado, así que decían que el
  // contrato ya existía quince días antes de desplegarse. Una optimización que se apoya en una
  // respuesta falsa recorta justo lo que había que leer, así que se recorre la ventana entera.

  // Los tramos se calculan enteros y se piden EN PARALELO por lotes. En serie, cubrir unos días de
  // HyperEVM eran decenas de idas y vueltas seguidas y la pantalla se quedaba pensando; el trabajo
  // es el mismo, pero esperarlo de ocho en ocho se nota.
  const ventanas: Array<[bigint, bigint]> = [];
  let hasta = ultimo;
  while (hasta > suelo && ventanas.length < TOPE_TRAMOS) {
    const desde = hasta > TRAMO && hasta - TRAMO > suelo ? hasta - TRAMO : suelo;
    ventanas.push([desde, hasta]);
    if (desde <= suelo) { hasta = suelo; break; }
    hasta = desde - 1n;
  }

  const logs: LogEncolada[] = [];
  const LOTE = 8;
  for (let i = 0; i < ventanas.length; i += LOTE) {
    const trozo = await Promise.all(
      ventanas.slice(i, i + LOTE).map(([desde, aDonde]) =>
        cliente
          .getLogs({ ...filtro, fromBlock: desde, toBlock: aDonde })
          .then((r) => r as LogEncolada[])
          // Un tramo que falla no puede tumbar a los demás: se pierde ESE trozo de historia, y eso
          // ya lo cuenta `completa`.
          .catch(() => [] as LogEncolada[])
      )
    );
    for (const l of trozo) logs.push(...l);
  }

  // Los tramos van de nuevo a viejo; el resto del hook espera el orden de la cadena.
  logs.sort((a, b) => Number((a.blockNumber ?? 0n) - (b.blockNumber ?? 0n)));
  return { logs, completa: hasta <= suelo };
}

/**
 * Todo lo que está anunciado y todavía vivo en el timelock de esta red.
 *
 * DE DÓNDE SALE. El contrato guarda `id => cuando`, y el id es un hash: desde el almacenamiento no
 * se puede reconstruir QUÉ se anunció ni, por tanto, ejecutarlo — `ejecutar` pide destino y datos
 * enteros. Eso solo está en el evento `Encolada`, así que la cola se arma leyendo los eventos y se
 * CONFIRMA uno a uno contra `estado(id)`, que es lo que dice la verdad: una propuesta cancelada o ya
 * ejecutada sigue teniendo su evento para siempre, pero su `listaEspera` vale 0.
 *
 * Un mismo id puede aparecer varias veces (se puede volver a anunciar lo que caducó): vale el
 * ÚLTIMO evento, que es el que fijó la hora vigente.
 */
export function usePendientes(red: RedKeel | undefined, timelock: Address | undefined) {
  const config = useConfig();
  const activo = !!red && !!timelock && timelock !== "0x0000000000000000000000000000000000000000";

  // EL HISTORIAL VA APARTE, Y ESA ES LA CLAVE DEL COSTE. Recorrer los eventos puede ser una petición
  // (Arbitrum, Robinhood) o ciento y pico (HyperEVM, que no deja rangos grandes). Hacerlo en cada
  // refresco sería insostenible; hacerlo una vez y refrescar solo `estado(id)` —un multicall— deja
  // la pantalla viva sin castigar a un RPC público. Se relee al anunciar o aplicar algo, que es
  // cuando de verdad cambia: `useAccionesGobierno` invalida esta clave.
  const historial = useQuery({
    queryKey: ["logs-timelock", red?.clave, timelock],
    enabled: activo,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const cliente = getPublicClient(config, { chainId: red!.idCadena })!;
      // Los eventos van por `rpcLogs` donde lo hay: el RPC de lectura de HyperEVM corta `eth_getLogs`
      // en 1000 bloques y el de respaldo de Arbitrum lo rechaza desde el bloque 0. Ver networks.ts.
      // Se usa el helper `clienteLogs`, que ya devuelve un `PublicClient` de tipo ancho: sin él, al
      // entrar Base (cadena OP-stack) en `cadenas` el cliente inferido traía las variantes de
      // transacción del rollup y dejaba de encajar en el `PublicClient` que espera `leerEncoladas`.
      const paraLogs = clienteLogs(red!);

      const [delay, gracia] = await Promise.all([
        cliente.readContract({ address: timelock!, abi: keelTimelockAbi, functionName: "delay" })
          .catch(() => 0),
        cliente.readContract({ address: timelock!, abi: keelTimelockAbi, functionName: "gracia" })
          .catch(() => 0),
      ]);

      const { logs, completa } = await leerEncoladas(
        paraLogs,
        timelock!,
        // Nada anunciado antes de `delay + gracia` puede seguir vivo: pasado ese plazo o se ejecutó
        // o caducó. Es el suelo natural de la búsqueda, y el que hace que esto termine.
        Number(delay) + Number(gracia)
      );

      // El último evento de cada id manda: reencolar tras caducar emite otro con hora nueva.
      const porId = new Map<string, LogEncolada>();
      for (const l of logs) if (l.args.id) porId.set(l.args.id, l);
      return { anuncios: [...porId.values()], completa, gracia: Number(gracia) };
    },
  });

  const h = historial.data;
  const consulta = useQuery({
    queryKey: ["pendientes", red?.clave, timelock, h?.anuncios.length ?? -1],
    enabled: activo && !!h,
    refetchInterval: 60_000,
    queryFn: async (): Promise<{ propuestas: Pendiente[]; completa: boolean }> => {
      const cliente = getPublicClient(config, { chainId: red!.idCadena })!;
      if (h!.anuncios.length === 0) return { propuestas: [], completa: h!.completa };

      const estados = await cliente.multicall({
        allowFailure: true,
        contracts: h!.anuncios.map((l) => ({
          address: timelock!,
          abi: keelTimelockAbi,
          functionName: "estado" as const,
          args: [l.args.id!] as const,
        })),
      });

      const salida: Pendiente[] = [];
      h!.anuncios.forEach((l, i) => {
        const r = estados[i];
        if (!r || r.status !== "success") return;
        const [encolada, cuando, lista, caducada] = r.result as [boolean, bigint, boolean, boolean];
        // Ejecutada o cancelada: el mapping quedó a 0 y esto ya no es una propuesta viva.
        if (!encolada) return;
        const t = traducirLlamada(l.args.datos!, red!);
        salida.push({
          id: l.args.id!,
          destino: l.args.destino!,
          datos: l.args.datos!,
          cuando: Number(cuando),
          caducaEn: Number(cuando) + h!.gracia,
          lista,
          caducada,
          titulo: t.titulo,
          detalle: t.detalle,
          cedeElMando: t.cedeElMando,
          destinoRaro: l.args.destino!.toLowerCase() !== red!.factory.toLowerCase()
            && l.args.destino!.toLowerCase() !== timelock!.toLowerCase(),
          rival: false,
          masNueva: false,
          // LA LISTA `implsConGestion` NO ES DECORACIÓN: es como la app sabe qué botones enseñarle a
          // un vault, porque los clones EIP-1167 no saben decir qué firmas entienden. Publicar una
          // implementación que no está ahí hace nacer vaults a los que la propia app no les ofrece
          // la gestión, y nada falla de forma visible. Se avisa aquí, que es antes de firmarlo.
          implDesconocida:
            t.implPublicada != null &&
            !red!.implsConGestion.some(
              (i) => i.toLowerCase() === t.implPublicada!.toLowerCase()
            ),
        });
      });

      // ¿HAY DOS PROPUESTAS VIVAS QUE HACEN LO MISMO? Pasa de verdad: el 2026-08-17 las tres redes
      // tenían DOS `setLogic` encoladas y listas a la vez, de dos despliegues distintos. El timelock
      // no las considera duplicadas —el id es el hash de la llamada ENTERA, y los argumentos son
      // otros— así que conviven sin protestar y gana simplemente la última que se aplique. Aplicar
      // la vieja después de la nueva es un retroceso silencioso, y sin avisar aquí no hay nada en la
      // pantalla que lo insinúe.
      const clave = (p: Pendiente) => `${p.destino.toLowerCase()}:${p.datos.slice(0, 10)}`;
      const grupos = new Map<string, Pendiente[]>();
      for (const p of salida) {
        const k = clave(p);
        grupos.set(k, [...(grupos.get(k) ?? []), p]);
      }
      for (const g of grupos.values()) {
        if (g.length < 2) continue;
        // La más nueva se señala: entre dos publicaciones de lógica, aplicar la vieja después de la
        // nueva es un retroceso, y las dos filas se ven idénticas si nada las distingue.
        const ultima = g.reduce((a, b) => (b.cuando > a.cuando ? b : a));
        for (const p of g) {
          p.rival = true;
          p.masNueva = p === ultima;
        }
      }

      // Primero lo aplicable; entre rivales, la MÁS NUEVA arriba —es la que se querrá aplicar, y
      // dejarla debajo pone el botón equivocado a mano—; y si no, lo que antes vence.
      salida.sort(
        (a, b) =>
          Number(b.lista) - Number(a.lista) ||
          Number(b.masNueva) - Number(a.masNueva) ||
          a.cuando - b.cuando
      );
      return { propuestas: salida, completa: h!.completa };
    },
  });

  return {
    data: consulta.data,
    // Leer el historial es la parte lenta: mientras dure, la pantalla dice que está leyendo en vez
    // de enseñar «no hay nada anunciado», que sería mentira a medias.
    isLoading: historial.isLoading || (activo && !consulta.data),
  };
}

/** Las dos firmas del gobierno: anunciar y, un día después, aplicar. */
export function useAccionesGobierno(red: RedKeel | undefined, timelock: Address | undefined) {
  const config = useConfig();
  const qc = useQueryClient();
  const { address } = useAccount();
  const [haciendo, setHaciendo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState<string | null>(null);

  async function firmar(nombre: string, fn: () => Promise<`0x${string}`>) {
    setError(null);
    setHecho(null);
    setHaciendo(nombre);
    // UNA VEZ FIRMADA, LA TRANSACCION YA ESTA EN LA RED. Lo que venga despues —esperar el recibo,
    // refrescar la pantalla— puede fallar por el RPC sin que eso deshaga nada. Pintarlo como "fallo"
    // es el peor error posible aqui: invita a firmar OTRA VEZ algo que ya se hizo. Paso el
    // 2026-08-15 en Arbitrum: el RPC publico contesto "Invalid parameters" al pedir el recibo, la
    // pantalla dijo que habia fallado y la propuesta estaba encolada perfectamente.
    let hash: `0x${string}` | undefined;
    try {
      // LA WALLET TIENE QUE ESTAR EN ESTA RED. Si no, wagmi corta con "the current chain of the
      // connector does not match" — un mensaje en inglés que no dice qué hacer. Se pide el cambio
      // antes de firmar, que es lo que el usuario iba a tener que hacer a mano de todos modos.
      if (getAccount(config).chainId !== red!.idCadena) {
        await switchChain(config, { chainId: red!.idCadena });
      }
      hash = await fn();
      await waitForTransactionReceipt(config, { hash, chainId: red!.idCadena });
      setHecho(nombre);
    } catch (e) {
      if (hash) {
        setHecho(nombre);
        setError(
          `Se firmó y se envió (${hash.slice(0, 10)}…), pero no pude confirmarlo con el RPC: ` +
            `${traducir(e)} NO vuelvas a firmar: casi seguro ya está hecho. Espera unos segundos y ` +
            `mira el estado de arriba, que se lee de la cadena.`
        );
      } else {
        setError(traducir(e));
      }
    } finally {
      // El estado se relee SIEMPRE, salga como salga: es lo único que dice la verdad.
      await qc.invalidateQueries({ queryKey: ["propuesta"] });
      // El historial también: acabamos de crear (o gastar) un anuncio, y es el único momento en que
      // merece la pena volver a recorrer los eventos.
      await qc.invalidateQueries({ queryKey: ["logs-timelock"] });
      await qc.invalidateQueries({ queryKey: ["pendientes"] });
      await qc.invalidateQueries({ queryKey: ["gobierno"] });
      setHaciendo(null);
    }
  }

  const datosSetLogic = (impl: string, logica: string) =>
    encodeFunctionData({
      abi: keelVaultFactoryAbi,
      functionName: "setLogic",
      args: [impl as Address, logica as Address],
    });

  const escribir = async (functionName: "encolar" | "ejecutar", args: readonly unknown[]) => {
    const w = await getWalletClient(config, { chainId: red!.idCadena });
    return w.writeContract({
      address: timelock!, abi: keelTimelockAbi, functionName, args, account: address!,
    } as never);
  };

  return {
    haciendo, error, hecho,
    limpiar: () => { setError(null); setHecho(null); },

    encolar: (impl: string, logica: string) =>
      firmar("encolar", () => escribir("encolar", [red!.factory, datosSetLogic(impl, logica)])),

    ejecutar: (impl: string, logica: string) =>
      firmar("ejecutar", () => escribir("ejecutar", [red!.factory, datosSetLogic(impl, logica)])),

    /**
     * Aplica una propuesta de la cola TAL Y COMO SE ANUNCIÓ.
     *
     * Destino y datos vienen del evento `Encolada`, no de reescribir las direcciones en dos campos.
     * Eso no es comodidad: el id es `keccak(destino, datos)`, así que un solo carácter distinto al
     * teclear es OTRA propuesta y `ejecutar` revierte con `NoEncolada` sobre algo que sí existe.
     * Copiando el calldata del evento, eso no puede pasar.
     */
    ejecutarLlamada: (destino: Address, datos: `0x${string}`, etiqueta = "ejecutar") =>
      firmar(etiqueta, () => escribir("ejecutar", [destino, datos])),
  };
}
